const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.zedule.app";

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json", ...options?.headers },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new ApiError(
      res.status,
      (body as { error?: { message?: string } })?.error?.message ?? res.statusText,
      (body as { error?: { code?: string } })?.error?.code ?? "UNKNOWN",
    );
    throw err;
  }
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ─── Business ────────────────────────────────────────────────────────────────

export type BookingTemplate = "classic" | "provider" | "datefirst" | "simple" | "catalog";
export type MeetingMode = "in_person" | "video" | "phone";

export interface BusinessInfo {
  name: string;
  tagline: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  website_url: string | null;
  logo_url: string | null;
  cover_url: string | null;
  primary_color: string | null;
  theme: string | null;
  booking_template: BookingTemplate;
  font_family: string;
  corner_radius: string;
  show_staff_names: boolean;
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    pincode: string | null;
  };
  google_place_id: string | null;
  timezone: string;
  slot_interval_min: number;
  advance_booking_days: number;
  min_notice_hours: number;
  cancellation_hours: number;
}

export async function fetchBusinessInfo(slug: string): Promise<BusinessInfo> {
  return apiFetch<BusinessInfo>(`/api/${slug}/info`);
}

// ─── Services ────────────────────────────────────────────────────────────────

export interface ServiceInfo {
  id: string;
  name: string;
  duration_min: number;
  price_cents: number | null;
  currency: string;
  meeting_mode: MeetingMode;
  is_active?: boolean;
}

export async function fetchServices(slug: string): Promise<ServiceInfo[]> {
  return apiFetch<ServiceInfo[]>(`/api/${slug}/services`);
}

// ─── Staff ───────────────────────────────────────────────────────────────────

export interface StaffInfo {
  id: string;
  name: string;
  avatar_url: string | null;
  is_active: boolean;
}

export async function fetchStaff(slug: string): Promise<StaffInfo[]> {
  return apiFetch<StaffInfo[]>(`/api/${slug}/staff`);
}

// ─── Slots ───────────────────────────────────────────────────────────────────

// API returns time-of-day slots merged across staff; availability pre-computed server-side.
export interface Slot {
  start: string;     // "HH:mm"
  end: string;       // "HH:mm"
  available: boolean;
}

export async function fetchSlots(
  slug: string,
  date: string,
  serviceIds: string[],
  staffId?: string,
): Promise<Slot[]> {
  const params = new URLSearchParams({ date, service_ids: serviceIds.join(",") });
  if (staffId) params.set("staff_id", staffId);
  const res = await fetch(`${API_BASE}/api/${slug}/slots?${params}`);
  if (!res.ok) throw new Error("Failed to load slots");
  return res.json();
}

// ─── Booking ─────────────────────────────────────────────────────────────────

export interface CreateBookingPayload {
  service_ids: string[];
  staff_id?: string;
  date: string;            // "YYYY-MM-DD"
  start_time: string;      // "HH:mm"
  meeting_mode?: string;
  customer: {
    name: string;
    email: string;
    phone: string;
    notes?: string;
  };
}

export interface BookingConfirmation {
  appointment_id: string;
  cancel_token: string;
  details: {
    service_ids: string[];
    staff_id: string;
    date: string;
    start_time: string;
    total_duration_min: number;
    total_price_cents: number;
    meeting_mode: string;
  };
  requires_deposit?: boolean;
  checkout_url?: string;
}

export async function createBooking(
  slug: string,
  payload: CreateBookingPayload,
): Promise<BookingConfirmation> {
  return apiFetch<BookingConfirmation>(`/api/${slug}/book`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ─── Cancel ──────────────────────────────────────────────────────────────────

export interface CancelInfo {
  id: string;
  service_name: string;
  staff_name: string;
  starts_at: string;
  ends_at: string;
  status: string;
  can_cancel: boolean;
}

export async function fetchCancelInfo(
  slug: string,
  token: string,
): Promise<CancelInfo> {
  return apiFetch<CancelInfo>(`/api/${slug}/cancel/${token}`);
}

export async function cancelBooking(
  slug: string,
  token: string,
): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/api/${slug}/cancel/${token}`, {
    method: "POST",
  });
}
